import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Close16,
  Export16,
  ArrowRight16,
  ChevronDown16,
  DocumentPdf16,
  Zip16,
  Automatic16,
  Map16
} from '@carbon/icons-react';
import {
  ViewingEmail,
  SCREEN_SIZE,
  TEAM_NAME,
  EnquiryEditFormValues
} from 'types';
import {
  AccordionSkeleton,
  Loading,
  Button,
  Link,
  InlineLoading,
  Tag,
  TooltipIcon,
  DatePicker
} from 'carbon-components-react';
import { Previewer, PdfViewer } from 'components';
import {
  toLocalDateTimeFormat,
  toLocalDateFormat,
  downloadFile,
  formFullAddress,
  checkIfExeFile
} from 'utilities/helper.util';
import {
  useComponentMount,
  useEventListener,
  useEmptyCallback
} from 'utilities/hooks.util';
import AssetOwnerList from './assetOwnerSection';
import AssetOwnerListDisabled from './assetOwnerSectionDisabled';
import { currentUser, selectScreenSize } from 'redux/session/selectors';
import { addAlert, updateUserStart } from 'redux/session/actions';
import { resetCustomTeamBaseMap } from 'redux/team/actions';
import { useSelector, useDispatch } from 'react-redux';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  getEnquiryDetailsStart,
  getReferralDetailsStart,
  setEnquiryReferralsDetails,
  updateEnquiryDetailsStart,
  downloadCombinedPDFStart,
  downloadCombinedZIPStart,
  downloadPartialZipStart,
  viewAttachmentStart,
  viewAttachmentSuccess,
  downloadAttachmentStart,
  setPreviewContent,
  editEnquiryAutoRenewStart,
  editEnquiryAutoRenewReset,
  archiveEnquiryStart,
  uploadAttachmentStart,
  deleteAttachmentStart,
  clearDeleteAttachmentErrors,
  clearUploadAttachmentErrors,
  updateAttachmentStart,
  updateAttachmentClear,
  getEnquiryAutoRenewSeriesStart,
  addEventStart,
  uploadAttachmentFailure
} from './localStore/actions';
import { PreviewContent } from './localStore/types';
import StatusIcon from '../StatusIcon';
import EmailsViewer from './emailsViewer';
import AutoRenew from './autoRenew';
import UpgradePlanSection from './upgradePlanSection';
import MoveEnquiryToTeam from './moveEnquiryToTeam';
import MoreActionsPanel from './moreActionsPanel';
import EditForm from './editForm';
import DirectAttachmentList from './directAttachments';
import ExcludeFilesModal from './excludeFilesModal';
import ArchiveSeries from './archiveSeries';
import PermalinkSection from './permalinkSection';
import createUrlBuilder from 'utilities/url.builder.utils';
import { translateEnquiryStatus } from 'utilities/helper.util';
import { getListAnnouncementsStart } from 'redux/announcement';
import { v4 as UUIDV4 } from 'uuid';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import DOMPurify from 'dompurify';

const getResponsesStatus = (assetOwner: any) => {
  if (assetOwner?.length > 0) {
    const found = assetOwner.find((ao: any) => ao.status === 'received');
    if (found) {
      return false;
    }
  }
  return true;
};

const getElementPos = (div: HTMLDivElement, screenSize?: string) => {
  const parentRect = div.offsetParent?.getBoundingClientRect();
  const divRect = div.getBoundingClientRect();
  const offsetTop = divRect.top - (parentRect?.top || 0);
  const offsetLeft = divRect.left - (parentRect?.left || 0);
  const marginTop = 18; // margin 1.125rem
  const marginLeft = screenSize && screenSize === SCREEN_SIZE.SM ? 16 : 32; // margin 1rem or 2rem
  const top = offsetTop - marginTop;
  const left = offsetLeft - marginLeft;

  return [top, left];
};

const getDocType = (
  contentType: string = '',
  fileName: string = ''
): string => {
  if (contentType.match(/image/)) {
    return 'Image';
  } else if (contentType.match(/pdf/) || fileName.match(/\.pdf/)) {
    return 'PDF';
  } else if (contentType.match(/vnd\.(?:(?:openxml)|(?:ms-))/i)) {
    return 'Document';
  } else {
    return 'File';
  }
};

const renderPreviewerContent = (
  type: string,
  content: string,
  name: string | undefined = ''
) => {
  switch (type) {
    case 'Email':
      return (
        <div
          className="email-content-previewer"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(content)
          }}
        />
      );
    case 'Image':
      return (
        <div className="image-previewer">
          <img crossOrigin="" src={content} alt={name} />
        </div>
      );
    case 'PDF':
      return <PdfViewer fileUrl={content} />;
    case 'Document':
      return (
        <iframe
          className="doc-previewer"
          title={name}
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            content
          )}`}
          frameBorder={'no'}
        />
      );
    default:
      return (
        <div className="empty-previewer">
          This attachment cannot be displayed.
        </div>
      );
  }
};

const EnquiryDetails: React.FC<Props> = ({
  handleCloseIconClick,
  enquiryIdDetails,
  setEnquiryIdDetails,
  isListView,
  isAnonymousView = false,
  anonymousEnquiryDetailsData,
  vhToken,
  plToken,
  setSearchText,
  setSearchInputValue,
  refreshMap,
  setIsListView
}) => {
  const dispatch = useDispatch();
  const { REACT_APP_BASE_URL } = process.env;
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<DatePicker>(null);
  const moreActionSectionRef = useRef<HTMLDivElement>(null);
  const waitingUpdateEmail = useRef<number | undefined>();
  const waitingArchivingEnquiry = useRef<boolean>(false);
  const user = useSelector(currentUser);
  const screenSize = useSelector(selectScreenSize);
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const {
    previewContent,
    currentAttachment,
    enquiryDetails,
    enquiryReferrals,
    isLoadingEnquiryDetails,
    combinedPDFDownloadURL,
    isDownloadingCombinedPDF,
    combinedZIPDownloadData,
    isDownloadingCombinedZIP,
    partialZipDownloadData,
    isDownloadingPartialZip,
    enquiryAutoRenew,
    editingEnquiryAutoRenew,
    uploadAttachmentErrors,
    uploadingAttachments,
    deletingAttachments,
    deleteAttachmentErrors,
    updateEnquiryError,
    updatingEnquiry,
    updateAttachments,
    isUpdatingAttachments,
    enquiryAutoRenewSeries,
    isArchivingEnquiry,
    archivedEnquiry
  } = localStore;

  const [referrals, setReferrals] = useState<any[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [moreActionPos, setMoreActionPos] = useState<number[]>([]);
  const [filteredEnquiryDetails, setFilteredEnquiryDetails] = useState<any>('');
  const [jobId, setJobId] = useState<number>(0);
  const [disableDownloadButton, setDisableDownloadButton] =
    useState<boolean>(true);
  const [buttonClickedId, setButtonClickedId] = useState<number>(0);
  const [enquiryDetailsData, setEnquiryDetailsData] = useState<any>('');
  const [confirmationPdfId, setConfirmationPdfId] = useState<
    number | undefined
  >();
  const [selectedEmail, setSelectedEmail] = useState<
    ViewingEmail | undefined
  >();
  const [showAutoRenew, setShowAutoRenew] = useState<any>('');
  const [showMoreActions, setShowMoreActions] = useState<boolean>(false);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [mapImageURL, setMapImageURL] = useState<string>('');
  const [mapImageLoaded, setMapImageLoaded] = useState<boolean>(false);
  const [needRefresh, setNeedRefresh] = useState<boolean>(false);
  const [showMoveEnquiryToTeam, setShowMoveEnquiryToTeam] =
    useState<boolean>(false);
  const [excludeFilesModalOpen, setExcludeFilesModalOpen] =
    useState<boolean>(false);
  const [excludeFilesModalData, setExcludeFilesModalData] = useState<{
    orgName: string;
    files: Array<string>;
    enquiryId: number;
  }>({ orgName: '', files: [], enquiryId: 0 });
  const [AOAttachments, setAOAttachments] = useState<Array<any>>([]);
  const [showAutoRenewItemMenu, setShowAutoRenewItemMenu] =
    useState<boolean>(false);
  const [showEnableAutoRenewMenu, setShowEnableAutoRenewMenu] =
    useState<boolean>(false);
  const [showARIcon, setShowARIcon] = useState<boolean>(false);
  const [showArchiveSeries, setShowArchiveSeries] = useState<boolean>(false);
  const [seriesIsArchive, setSeriesIsArchive] = useState<boolean>(false);
  const [showPermalink, setShowPermalink] = useState<boolean>(false);
  const [enquiryId, setEnquiryId] = useState<number>(0);
  const [uploadFrom, setUploadFrom] = useState<string>('');

  const handleDownloadPDF = () => {
    const params = {
      enquiryId: enquiryIdDetails || filteredEnquiryDetails.id,
      isAnonymousView,
      plToken: isAnonymousView ? plToken : undefined,
      vhToken: isAnonymousView ? vhToken : undefined
    };
    localDispatch(downloadCombinedPDFStart(params));
  };

  const handleDownloadZIP = () => {
    const params = {
      enquiryId: enquiryIdDetails || filteredEnquiryDetails.id,
      isAnonymousView,
      plToken: isAnonymousView ? plToken : undefined,
      vhToken: isAnonymousView ? vhToken : undefined
    };
    localDispatch(downloadCombinedZIPStart(params));
  };

  const handleDownloadPartialZip = (
    aoId: number,
    seqNo: number,
    aoName: string
  ) => {
    const params = {
      enquiryId: enquiryIdDetails || filteredEnquiryDetails.id,
      aoId,
      jobId,
      seqNo,
      aoName,
      isAnonymousView,
      plToken: isAnonymousView ? plToken : undefined,
      vhToken: isAnonymousView ? vhToken : undefined
    };
    localDispatch(downloadPartialZipStart(params));
    setButtonClickedId(aoId);
  };

  const viewAttachment = (id: number, attachments: Array<any> = []) => {
    const params = {
      id,
      isAnonymousView,
      plToken: isAnonymousView ? plToken : undefined,
      vhToken: isAnonymousView ? vhToken : undefined
    };
    localDispatch(viewAttachmentStart(params, false));
    if (attachments?.length > 0) {
      setAOAttachments(attachments);
    }
  };

  const uploadAttachment = async (
    enquiryId: number,
    file: File,
    assetOwnerId?: number
  ) => {
    if (assetOwnerId) {
      waitingUpdateEmail.current = assetOwnerId;
    }

    if (uploadAttachmentErrors.length > 0) {
      localDispatch(
        clearUploadAttachmentErrors(uploadAttachmentErrors[0].uuid)
      );
    }

    // check for file type and extension and show error if it's an executable file
    const validFileType = await checkIfExeFile(file);
    if (!validFileType) {
      const uuid = UUIDV4();
      const error = `Unsupported file type`;
      !assetOwnerId ? setUploadFrom('enquiry') : setUploadFrom('ao');
      return localDispatch(
        uploadAttachmentFailure(uuid, error, enquiryId, assetOwnerId)
      );
    }

    localDispatch(uploadAttachmentStart(enquiryId, file, assetOwnerId));
  };

  const deleteAttachment = (
    id: number,
    enquiryId: number,
    assetOwnerId?: number
  ) => {
    if (assetOwnerId) {
      waitingUpdateEmail.current = assetOwnerId;
    }
    localDispatch(deleteAttachmentStart(id, enquiryId, assetOwnerId));
  };

  const downloadAttachment = (
    url: string,
    fileName: string,
    contentType: string
  ) => {
    localDispatch(downloadAttachmentStart(url, fileName, contentType));
  };

  const previewDocument = useEmptyCallback(
    (content: PreviewContent | undefined) => {
      localDispatch(setPreviewContent(content));
    }
  );

  const getARSeriesData = useEmptyCallback((enquiryId: number) => {
    localDispatch(getEnquiryAutoRenewSeriesStart({ enquiryId }));
  });

  const archiveEnquiry = (
    enquiryId: number,
    enquiryIdArray: number[],
    isArchive: boolean
  ) => {
    setNeedRefresh(true);
    localDispatch(
      archiveEnquiryStart({ enquiryId, enquiryIdArray, isArchive })
    );
  };

  const updateEnquiryDetails = (
    enquiryId: number,
    value: EnquiryEditFormValues
  ) => {
    const data = {
      user_reference: value.userReference,
      expire_at: value.expireAt
    };
    setNeedRefresh(true);
    localDispatch(updateEnquiryDetailsStart({ enquiryId, data }));
  };

  const getEnquiryMapURL = useEmptyCallback((params: any) => {
    const urlBuilder = createUrlBuilder(
      `${REACT_APP_BASE_URL}/enquiries/${enquiryIdDetails}/image`
    );
    urlBuilder.addParameter('image', true);
    urlBuilder.addParameter('imgW', params.imageWidth);
    urlBuilder.addParameter('imgH', params.imageHeight);
    return urlBuilder.toString();
  });

  const handleAutoRenewingClick = () => {
    const autoRenewData = {
      enquiryId: filteredEnquiryDetails.id,
      enquiryLodgedAt: filteredEnquiryDetails.enquiry_at,
      jobStartAt: filteredEnquiryDetails.start_at,
      jobEndAt: filteredEnquiryDetails.is_edit
        ? filteredEnquiryDetails.end_at_edited
        : filteredEnquiryDetails.end_at,
      autoRenew: filteredEnquiryDetails.is_edit
        ? filteredEnquiryDetails.auto_renew_edited
        : filteredEnquiryDetails.auto_renew,
      autoRenewDate:
        filteredEnquiryDetails.is_edit && !isAnonymousView
          ? filteredEnquiryDetails.auto_renew_date_edited
          : filteredEnquiryDetails.auto_renew_date,
      autoRenewDone: filteredEnquiryDetails.auto_renew_done,
      autoRenewEnquiryId: filteredEnquiryDetails.auto_renew_enquiry_id,
      autoRenewEvery: filteredEnquiryDetails.is_edit
        ? filteredEnquiryDetails.auto_renew_every_edited
        : filteredEnquiryDetails.auto_renew_every,
      autoRenewUntil: filteredEnquiryDetails.is_edit
        ? filteredEnquiryDetails.auto_renew_until_edited
        : filteredEnquiryDetails.auto_renew_until,
      isSentinel: filteredEnquiryDetails.is_sentinel,
      isArchived: !!filteredEnquiryDetails.archived_at,
      internalCreate: filteredEnquiryDetails.internal_create,
      isAnonymousView,
      isExpired: filteredEnquiryDetails.is_expired,
      isEdit: filteredEnquiryDetails.is_edit,
      isNewAutoRenew: showEnableAutoRenewMenu
    };
    setShowAutoRenew(autoRenewData);
  };

  const handleSaveEditAutoRenew = (values: any) => {
    const params = {
      enquiryId: filteredEnquiryDetails.id,
      editId: filteredEnquiryDetails.id,
      isDuplicate: false,
      isEdit: true,
      autoRenew: values.autoRenew,
      autoRenewEvery: values.autoRenewEvery,
      autoRenewUntil: values.autoRenewUntil,
      completionDate: values.jobEndDate
    };
    setNeedRefresh(true);
    localDispatch(editEnquiryAutoRenewStart(params));
  };

  const handleAutoRenewClose = () => {
    setShowAutoRenew(undefined);
    localDispatch(editEnquiryAutoRenewReset());
  };

  const handleMoveEnquiryToTeam = () => {
    setShowMoveEnquiryToTeam(true);
  };

  const handleMoveEnquiryToTeamClose = () => {
    setShowMoveEnquiryToTeam(false);
  };

  const handleRefreshCurrentTeamView = () => {
    handleCloseIconClick(true);
  };

  const handleGoToEnquiry = (
    projectId: number,
    enquiryId: number,
    jobId: number | string
  ) => {
    handleCloseIconClick(false);
    // set the new team
    dispatch(resetCustomTeamBaseMap());
    dispatch(
      updateUserStart({ default_project: projectId > 0 ? projectId : null })
    );
    // set the details panel to open the enquiry
    setTimeout(() => {
      setSearchText && setSearchText(jobId);
    }, 500);
    setTimeout(() => {
      setSearchInputValue && setSearchInputValue(jobId);
    }, 750);
    setTimeout(() => {
      setEnquiryIdDetails && setEnquiryIdDetails(enquiryId);
    }, 1000);
  };

  const handleArchiveSeriesClick = (isArchive: boolean) => {
    setSeriesIsArchive(isArchive);
    setShowArchiveSeries(showArchiveSeries ? false : true);
  };

  const handlePreviewerClose = () => {
    previewDocument(undefined);
    localDispatch(viewAttachmentSuccess(undefined));
  };

  const handleClickOutside = useCallback(
    (evt: any) => {
      const systemToastAlert = document
        .getElementsByClassName('system-alert')
        .item(0);
      const previewer = document
        .getElementsByClassName('previewer-modal')
        .item(0);
      const excludeFilesModal = document
        .getElementsByClassName('exclude-files-modal')
        .item(0);
      if (containerRef.current && evt.target) {
        if (
          !containerRef.current.contains(evt.target) &&
          !calendarRef.current &&
          !previewer &&
          !systemToastAlert &&
          !excludeFilesModal
        ) {
          !isAnonymousView && handleCloseIconClick(needRefresh);
        }
      }
    },
    [containerRef, handleCloseIconClick, isAnonymousView, needRefresh]
  );

  const handleClickExcludeFilesLink = (
    orgName: string,
    enquiryId: number,
    attachments: any
  ) => {
    localDispatch(updateAttachmentClear({}));
    setExcludeFilesModalData({ orgName, files: attachments, enquiryId });
    setExcludeFilesModalOpen(!excludeFilesModalOpen);
  };

  const handleMoreActionsClick = useCallback(() => {
    if (moreActionSectionRef.current) {
      const pos = getElementPos(moreActionSectionRef.current, screenSize);
      setMoreActionPos(pos);
    }
    setShowMoreActions(true);
  }, [screenSize]);

  const handleResize = useCallback(() => {
    if (moreActionSectionRef.current) {
      const pos = getElementPos(moreActionSectionRef.current, screenSize);
      setMoreActionPos(pos);
    }
  }, [screenSize]);

  const handleExcludeButtonClick = (data: any) => {
    localDispatch(updateAttachmentStart(data));
  };

  const handleShareLinkButtonClick = () => {
    setShowPermalink(true);
    const params = {
      event_type: 'SHARE_BUTTON_CLICK'
    };
    localDispatch(addEventStart(params));
  };

  const handleViewOnSearchMapClick = () => {
    if (!isLoadingEnquiryDetails && enquiryDetailsData) {
      setIsListView && setIsListView(false);
      window.viewEnquiryOnMap = enquiryDetailsData.shape;
      if (window.loadedView && window.loadedView.ready === true) {
        goToEnquiry(enquiryDetailsData.shape);
      } else {
        window.viewEnquiryOnMap = enquiryDetailsData.shape;
      }
      if (screenSize && screenSize === SCREEN_SIZE.SM) {
        handleCloseIconClick();
      }
    }
  };

  const goToEnquiry = (shape: any) => {
    if (shape) {
      const { loadedMapModules } = window;
      const { Polygon } = loadedMapModules;
      const geometry = geojsonToArcGIS(shape);
      const polygon = new Polygon(geometry);
      window.loadedView.goTo(polygon.extent);
    }
  };

  const getReferralDetails = useEmptyCallback((data) => {
    localDispatch(getReferralDetailsStart(data));
  });

  const setFullReferrals = useEmptyCallback((data) => {
    localDispatch(setEnquiryReferralsDetails(data));
  });

  useEffect(() => {
    if (isAnonymousView && anonymousEnquiryDetailsData) {
      const assetOwners = anonymousEnquiryDetailsData.Assetowner.map(
        (assetOwner: any) => {
          return {
            ...assetOwner,
            isLoading: true
          };
        }
      );
      setFullReferrals(assetOwners);
      setEnquiryDetailsData(anonymousEnquiryDetailsData);
    } else if (!isAnonymousView && enquiryDetails) {
      if (typeof enquiryDetails === 'string') {
        handleCloseIconClick();
        dispatch(addAlert('error', enquiryDetails));
      } else {
        setEnquiryDetailsData(enquiryDetails);
      }
    }
  }, [
    anonymousEnquiryDetailsData,
    enquiryDetails,
    isAnonymousView,
    handleCloseIconClick,
    setFullReferrals,
    dispatch
  ]);

  useEffect(() => {
    if (!isDownloadingCombinedPDF && combinedPDFDownloadURL) {
      window.location.href = combinedPDFDownloadURL;
    }
  }, [isDownloadingCombinedPDF, combinedPDFDownloadURL]);

  useEffect(() => {
    if (!isDownloadingCombinedZIP && combinedZIPDownloadData) {
      const { blobData, fileName } = combinedZIPDownloadData;
      downloadFile(blobData, fileName, 'application/zip');
    }
  }, [isDownloadingCombinedZIP, combinedZIPDownloadData]);

  useEffect(() => {
    if (!isDownloadingPartialZip && partialZipDownloadData) {
      const { blobData, fileName } = partialZipDownloadData;
      downloadFile(blobData, fileName, 'application/zip');
    }
  }, [isDownloadingPartialZip, partialZipDownloadData]);

  useEffect(() => {
    if (!isLoadingEnquiryDetails && enquiryDetailsData) {
      setJobId(enquiryDetailsData.external_id);
      setEnquiryId(enquiryDetailsData.id);

      enquiryDetailsData.Assetowner.forEach((assetOwner: any) => {
        getReferralDetails({
          enquiryId: enquiryDetailsData.id,
          assetOwner,
          isAnonymousView,
          plToken,
          vhToken
        });
      });

      setUploadedFiles(enquiryDetailsData.Directattachment);

      setDisableDownloadButton(
        getResponsesStatus(enquiryDetailsData.Assetowner)
      );
      if (
        Array.isArray(enquiryDetailsData.Mailbody) &&
        enquiryDetailsData.Mailbody.length > 0
      ) {
        if (
          Array.isArray(enquiryDetailsData.Mailbody[0].Attachment) &&
          enquiryDetailsData.Mailbody[0].Attachment.length > 0
        ) {
          setConfirmationPdfId(enquiryDetailsData.Mailbody[0].Attachment[0].id);
        }
      }
      const isExpired = enquiryDetailsData.is_expired;
      const status = isExpired ? 'expired' : enquiryDetailsData.status;
      const address = formFullAddress(enquiryDetailsData.address);
      const filteredEnquiryDetails = {
        ...enquiryDetailsData,
        status,
        address
      };
      setFilteredEnquiryDetails(filteredEnquiryDetails);

      let showARIcon = false;
      let showARProperties = false;
      let showEnableAR = false;

      if (enquiryDetailsData.is_edit) {
        if (
          enquiryDetailsData.internal_create &&
          enquiryDetailsData.auto_renew_edited &&
          enquiryDetailsData.auto_renew_until_edited
        ) {
          if (!enquiryDetailsData.archived_at) {
            showARIcon = true;
          }
          showARProperties = true;
        } else if (
          enquiryDetailsData.internal_create &&
          !enquiryDetailsData.archived_at &&
          (enquiryDetailsData.auto_renew_until_edited ||
            enquiryDetailsData.auto_renew_until) &&
          !enquiryDetailsData.is_expired
        ) {
          showEnableAR = true;
        }
      } else {
        // this is for equiry with AR created in sentinel and also created with AR (originally)
        if (
          enquiryDetailsData.auto_renew ||
          (!enquiryDetailsData.internal_create &&
            enquiryDetailsData.auto_renew_until)
        ) {
          if (!enquiryDetailsData.archived_at) {
            showARIcon = true;
          }
          showARProperties = true;
        }
      }

      if (!isAnonymousView && enquiryDetailsData.internal_create) {
        if (
          !enquiryDetailsData.is_edit &&
          !enquiryDetailsData.auto_renew &&
          !enquiryDetailsData.archived_at &&
          !enquiryDetailsData.is_expired
        ) {
          showEnableAR = true;
        }
      }
      setShowAutoRenewItemMenu(showARProperties);
      setShowARIcon(showARIcon);
      setShowEnableAutoRenewMenu(showEnableAR);
    }
  }, [
    isLoadingEnquiryDetails,
    enquiryDetailsData,
    isAnonymousView,
    plToken,
    vhToken,
    getReferralDetails
  ]);

  useEffect(() => {
    if (enquiryReferrals.length > 0) {
      setReferrals(enquiryReferrals);
    }
  }, [enquiryReferrals]);

  useEffect(() => {
    if (!isLoadingEnquiryDetails && enquiryId && enquiryId !== 0) {
      const queryParams = {
        enquiryId: enquiryId
      };
      dispatch(getListAnnouncementsStart(queryParams));
    }
  }, [isLoadingEnquiryDetails, enquiryId, dispatch]);

  useEffect(() => {
    if (!isAnonymousView && !enquiryDetailsData?.is_ignore) {
      const mapURL = getEnquiryMapURL({ imageWidth: 416, imageHeight: 168 });
      setMapImageURL(mapURL);
    }
  }, [enquiryDetailsData, getEnquiryMapURL, isAnonymousView]);

  useEffect(() => {
    if (currentAttachment) {
      previewDocument({
        type: getDocType(
          currentAttachment?.content_type,
          currentAttachment?.filename
        ),
        title: currentAttachment?.presentation_name || 'Unknown',
        content: currentAttachment.url
      });
    }
  }, [currentAttachment, previewDocument]);

  useEffect(() => {
    if (selectedEmail === undefined) {
      waitingUpdateEmail.current = undefined;
    }
  }, [selectedEmail]);

  useEffect(() => {
    if (waitingUpdateEmail.current && referrals.length > 0) {
      referrals.forEach((r: any) => {
        if (r.id === waitingUpdateEmail.current) {
          setSelectedEmail({
            seqNo: r.seq_no,
            assetOwnerContact: r.phone !== 'N/A' ? r.phone : undefined,
            id: r.id,
            name: r.authority_name,
            bodies: r.Mailbody,
            uploadedFiles: r.Directattachment
          });
        }
      });
    }
  }, [referrals]);

  useEffect(() => {
    !isAnonymousView &&
      showAutoRenewItemMenu &&
      getARSeriesData(enquiryIdDetails);
  }, [
    showAutoRenewItemMenu,
    isAnonymousView,
    enquiryIdDetails,
    getARSeriesData
  ]);

  const renderMapAgain = useEmptyCallback(
    () => typeof refreshMap === 'function' && refreshMap()
  );

  useEffect(() => {
    if (isArchivingEnquiry && !waitingArchivingEnquiry.current) {
      waitingArchivingEnquiry.current = true;
    }
    if (!isArchivingEnquiry && waitingArchivingEnquiry.current) {
      waitingArchivingEnquiry.current = false;
      renderMapAgain();
    }
  }, [isArchivingEnquiry, renderMapAgain]);

  useComponentMount(() => {
    const params = { enquiryId: enquiryIdDetails };
    !isAnonymousView && localDispatch(getEnquiryDetailsStart(params));
  });

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);
  useEventListener('resize', handleResize, window);

  return (
    <>
      {previewContent && (
        <Previewer
          open={true}
          isDownload={previewContent.type !== 'Email'}
          canDelete={currentAttachment?.is_manual && !isAnonymousView}
          currentAttachment={currentAttachment}
          enquiryId={filteredEnquiryDetails.id}
          AOAttachments={AOAttachments}
          isUpdatingAttachments={isUpdatingAttachments}
          updateAttachments={updateAttachments}
          onNextPreviousButtonClick={viewAttachment}
          isAnonymousView={isAnonymousView}
          title={previewContent.title}
          type={previewContent.type}
          onClose={() => handlePreviewerClose()}
          onDownload={() =>
            previewContent.type !== 'Email' &&
            currentAttachment &&
            downloadAttachment(
              currentAttachment.url,
              currentAttachment.filename,
              currentAttachment.content_type
            )
          }
          onDelete={() => {
            deleteAttachment(
              currentAttachment.id,
              filteredEnquiryDetails.id,
              currentAttachment.assetowner_id
            );
            handlePreviewerClose();
          }}
          onExcludeInclude={handleExcludeButtonClick}
        >
          {renderPreviewerContent(
            previewContent.type,
            previewContent.content,
            currentAttachment?.presentation_name
          )}
        </Previewer>
      )}
      <div className="enquiry-details-modal">
        <section
          ref={containerRef}
          className={`enquiry-details-modal-main${
            showPermalink ? ' show-permalink' : ''
          }`}
        >
          <div className="close-button-container">
            {!isAnonymousView && (
              <div
                className="close-button-body"
                onClick={() => handleCloseIconClick(needRefresh)}
              >
                <Close16 />
              </div>
            )}
          </div>
          {enquiryIdDetails !== 0 &&
            !isLoadingEnquiryDetails &&
            filteredEnquiryDetails && (
              <div className="modal-container">
                <div className="modal-title-container">
                  <Tag
                    type="purple"
                    className={`modal-title-tag ${filteredEnquiryDetails.status.toLowerCase()}`}
                  >
                    {`Job ${filteredEnquiryDetails.external_id}`}
                  </Tag>
                  {selectedEmail?.seqNo && (
                    <Tag type="cyan" className="referal-tag">
                      {`Ref ${selectedEmail.seqNo}`}
                    </Tag>
                  )}
                  <div className="auto-renew-icon">
                    {showARIcon && (
                      <TooltipIcon
                        tooltipText={`Auto-renews until ${toLocalDateFormat(
                          filteredEnquiryDetails.is_edit
                            ? filteredEnquiryDetails.auto_renew_until_edited
                            : filteredEnquiryDetails.auto_renew_until
                        )}`}
                        align={'center'}
                      >
                        <Automatic16 />
                      </TooltipIcon>
                    )}
                  </div>
                </div>
                {selectedEmail && (
                  <EmailsViewer
                    timezone={user?.timezone}
                    email={selectedEmail}
                    isAnonymousView={isAnonymousView}
                    onClose={() => setSelectedEmail(undefined)}
                    onViewAttachment={viewAttachment}
                    onPreviewOriginalEmail={previewDocument}
                    onUploadAttachment={(file: File, id?: number) =>
                      uploadAttachment(filteredEnquiryDetails.id, file, id)
                    }
                    uploadAttachmentErrors={uploadAttachmentErrors}
                    uploadingAttachments={uploadingAttachments}
                    deletingAttachments={deletingAttachments}
                    deleteAttachmentErrors={deleteAttachmentErrors}
                    onClearDeleteAttachmentErrors={(id: number) =>
                      localDispatch(clearDeleteAttachmentErrors(id))
                    }
                    onClearUploadAttachmentErrors={(id: string) =>
                      localDispatch(clearUploadAttachmentErrors(id))
                    }
                    onClickExcludeFilesLink={handleClickExcludeFilesLink}
                  />
                )}
                {showAutoRenew && (
                  <AutoRenew
                    autoRenewData={showAutoRenew}
                    onClose={handleAutoRenewClose}
                    onSaveAutoRenew={handleSaveEditAutoRenew}
                    calendarRef={calendarRef}
                    editingEnquiryAutoRenew={editingEnquiryAutoRenew || false}
                    enquiryAutoRenew={enquiryAutoRenew}
                  />
                )}
                {showEditForm && (
                  <EditForm
                    onClose={() => setShowEditForm(false)}
                    onSaveEnquiry={(val: EnquiryEditFormValues) =>
                      updateEnquiryDetails(filteredEnquiryDetails.id, val)
                    }
                    enquiryData={filteredEnquiryDetails}
                    updating={!!updatingEnquiry}
                    error={updateEnquiryError}
                    calendarRef={calendarRef}
                  />
                )}
                {showMoveEnquiryToTeam && (
                  <MoveEnquiryToTeam
                    onClose={handleMoveEnquiryToTeamClose}
                    onRefreshTeamView={handleRefreshCurrentTeamView}
                    enquiryTeamId={filteredEnquiryDetails.project_id}
                    enquiryId={filteredEnquiryDetails.id}
                    enquiryUserId={filteredEnquiryDetails.user_id}
                    jobId={filteredEnquiryDetails.external_id}
                    refreshMap={refreshMap}
                    closeDetailsPanel={handleCloseIconClick}
                    setNeedRefresh={setNeedRefresh}
                    onGoToEnquiry={handleGoToEnquiry}
                    isAutoRenew={showAutoRenewItemMenu}
                    enquiryAutoRenewSeries={enquiryAutoRenewSeries}
                  />
                )}
                {showArchiveSeries && (
                  <ArchiveSeries
                    onClose={handleArchiveSeriesClick}
                    onRefreshTeamView={handleRefreshCurrentTeamView}
                    enquiryId={filteredEnquiryDetails.id}
                    enquiryAutoRenewSeries={enquiryAutoRenewSeries}
                    isArchive={seriesIsArchive}
                    archiveEnquiry={archiveEnquiry}
                    isArchivingEnquiry={isArchivingEnquiry}
                    archivedEnquiry={archivedEnquiry}
                  />
                )}
                <div className="modal-content">
                  <section
                    className={`enquiry-status-map ${
                      filteredEnquiryDetails.is_ignore ? 'trial-user-limit' : ''
                    }`}
                  >
                    <div
                      className={`enquiry-status ${
                        isListView &&
                        !isAnonymousView &&
                        !filteredEnquiryDetails.is_ignore
                          ? ''
                          : 'enquiry-status-center'
                      }`}
                    >
                      <div
                        className={`enquiry-status-content ${
                          filteredEnquiryDetails.is_ignore
                            ? 'trial-user-limit'
                            : ''
                        }`}
                      >
                        <StatusIcon
                          status={filteredEnquiryDetails.status}
                          isIgnore={filteredEnquiryDetails.is_ignore}
                          archivedAt={filteredEnquiryDetails.archived_at}
                        />
                        &nbsp;&nbsp;
                        {translateEnquiryStatus(
                          filteredEnquiryDetails.status,
                          filteredEnquiryDetails.archived_at,
                          filteredEnquiryDetails.is_ignore,
                          true
                        )}
                      </div>
                      {screenSize && screenSize === SCREEN_SIZE.SM
                        ? isListView &&
                          !isAnonymousView &&
                          !filteredEnquiryDetails.is_ignore && (
                            <div
                              onClick={handleViewOnSearchMapClick}
                              className="view-on-map"
                            >
                              <Map16 />
                            </div>
                          )
                        : isListView &&
                          !isAnonymousView &&
                          !filteredEnquiryDetails.is_ignore && (
                            <div
                              onClick={handleViewOnSearchMapClick}
                              className="view-on-map"
                            >
                              View on search map
                            </div>
                          )}
                    </div>
                    {isListView &&
                      !isAnonymousView &&
                      !filteredEnquiryDetails.is_ignore && (
                        <div className="enquiry-mini-map">
                          <div className="enquiry-mini-map-content">
                            {!mapImageLoaded && <Loading withOverlay={false} />}
                            {mapImageURL && (
                              <img
                                src={mapImageURL || ''}
                                alt=""
                                onLoad={() => setMapImageLoaded(true)}
                                className={
                                  mapImageLoaded
                                    ? 'map-image mini-map-img'
                                    : 'mini-map-img'
                                }
                              />
                            )}
                          </div>
                        </div>
                      )}
                  </section>
                  {referrals.length <= 0 && <div className="divider" />}
                  <section className="asset-owners">
                    {referrals && !filteredEnquiryDetails.is_ignore && (
                      <AssetOwnerList
                        isLoading={
                          isLoadingEnquiryDetails && referrals?.length === 0
                        }
                        assetOwner={referrals}
                        handleDownloadPartialZip={handleDownloadPartialZip}
                        isDownloadingPartialZip={isDownloadingPartialZip}
                        buttonClickedId={buttonClickedId}
                        handleEmailOpen={(email: ViewingEmail) =>
                          setSelectedEmail(email)
                        }
                      />
                    )}
                    {referrals && filteredEnquiryDetails.is_ignore && (
                      <AssetOwnerListDisabled
                        isLoading={
                          isLoadingEnquiryDetails && referrals?.length === 0
                        }
                        assetOwner={referrals}
                      />
                    )}
                  </section>
                  {uploadedFiles.length > 0 && (
                    <section className="uploaded-files">
                      <DirectAttachmentList
                        files={uploadedFiles}
                        enquiryId={filteredEnquiryDetails.id}
                        uploadAttachmentErrors={uploadAttachmentErrors}
                        uploadingAttachments={uploadingAttachments}
                        deletingAttachments={deletingAttachments}
                        deleteAttachmentErrors={deleteAttachmentErrors}
                        onClearDeleteAttachmentErrors={(id: number) =>
                          localDispatch(clearDeleteAttachmentErrors(id))
                        }
                        onClearUploadAttachmentErrors={(id: string) =>
                          localDispatch(clearUploadAttachmentErrors(id))
                        }
                        onViewAttachment={viewAttachment}
                        uploadFrom={uploadFrom}
                      />
                    </section>
                  )}
                  {uploadedFiles.length === 0 &&
                    !!uploadingAttachments.find(
                      (up) => up.enquiryId === filteredEnquiryDetails.id
                    ) && (
                      <section className="uploaded-files">
                        <AccordionSkeleton count={1} open={false} />
                      </section>
                    )}
                  {!isAnonymousView && (
                    <>
                      <section className="enquiry-information section-spacing">
                        <div className="info-container">
                          <div className="info-title">My reference</div>
                          &nbsp;
                          <div>{filteredEnquiryDetails.user_reference}</div>
                        </div>
                        <div className="info-container">
                          <div className="info-title">Team</div>
                          &nbsp;
                          <div>
                            {filteredEnquiryDetails.Project
                              ? filteredEnquiryDetails.Project.name
                              : TEAM_NAME.PRIVATE}
                          </div>
                        </div>
                        <div className="info-container">
                          <div className="info-title">Job site</div>
                          &nbsp;
                          <div>{`${filteredEnquiryDetails.address}`}</div>
                        </div>
                        <div className="info-container">
                          <div className="info-title">Enquirer</div>
                          &nbsp;
                          <div>{`${filteredEnquiryDetails.Users?.first_name} ${filteredEnquiryDetails.Users?.last_name}`}</div>
                        </div>
                        <div className="info-container">
                          <div className="info-title">Enquiry date</div>
                          &nbsp;
                          <div>
                            {toLocalDateTimeFormat(
                              filteredEnquiryDetails.enquiry_at
                            )}
                          </div>
                        </div>
                        <div className="info-container">
                          <div className="info-title">Job dates</div>
                          &nbsp;
                          <div className="job-dates-span">
                            {toLocalDateFormat(filteredEnquiryDetails.start_at)}
                            &nbsp;
                            <ArrowRight16 />
                            &nbsp;
                            {toLocalDateFormat(filteredEnquiryDetails.end_at)}
                          </div>
                        </div>
                        <div className="info-container">
                          <div className="info-title">Expiry date</div>
                          &nbsp;
                          <div>
                            {toLocalDateFormat(
                              filteredEnquiryDetails.expire_at
                            )}
                          </div>
                        </div>
                      </section>

                      <div className="divider" />
                    </>
                  )}
                  {!filteredEnquiryDetails.is_ignore && (
                    <>
                      <section
                        ref={moreActionSectionRef}
                        className="confirmation-and-more-actions section-spacing"
                      >
                        {confirmationPdfId && (
                          <div className="confirmation-link-container">
                            <Link
                              onClick={() => viewAttachment(confirmationPdfId)}
                            >
                              BYDA confirmation
                            </Link>
                          </div>
                        )}
                        {!isAnonymousView && (
                          <div className="more-actions-link-container">
                            <Link onClick={handleMoreActionsClick}>
                              More actions
                              <ChevronDown16 />
                            </Link>
                          </div>
                        )}
                        {isAnonymousView && showAutoRenewItemMenu && (
                          <div className="more-actions-link-container">
                            <Link onClick={() => handleAutoRenewingClick()}>
                              Auto-renew properties
                            </Link>
                          </div>
                        )}
                      </section>
                      {uploadFrom === 'enquiry' &&
                        uploadAttachmentErrors.length > 0 && (
                          <section className="upload-attachment-error-message section-spacing">
                            {uploadAttachmentErrors[0].error}
                          </section>
                        )}
                      <div className="divider" />
                      <section
                        className={`download-buttons section-spacing${
                          filteredEnquiryDetails.permalink
                            ? ' has-permalink'
                            : ''
                        }`}
                      >
                        <Button
                          kind="tertiary"
                          size="default"
                          renderIcon={
                            isDownloadingCombinedPDF ? '' : DocumentPdf16
                          }
                          onClick={handleDownloadPDF}
                          disabled={
                            isDownloadingCombinedPDF ||
                            disableDownloadButton ||
                            isDownloadingCombinedZIP
                          }
                        >
                          {!isDownloadingCombinedPDF ? (
                            'Combined PDF'
                          ) : (
                            <InlineLoading
                              description={
                                !isDownloadingCombinedPDF
                                  ? 'Combined PDF'
                                  : 'Downloading...'
                              }
                              status={
                                !isDownloadingCombinedPDF
                                  ? 'finished'
                                  : 'active'
                              }
                              aria-live={'assertive'}
                            />
                          )}
                        </Button>
                        <Button
                          kind="tertiary"
                          size="default"
                          renderIcon={isDownloadingCombinedZIP ? '' : Zip16}
                          onClick={handleDownloadZIP}
                          disabled={
                            isDownloadingCombinedZIP ||
                            disableDownloadButton ||
                            isDownloadingCombinedPDF
                          }
                        >
                          {!isDownloadingCombinedZIP ? (
                            'Combined ZIP'
                          ) : (
                            <InlineLoading
                              description={
                                !isDownloadingCombinedZIP
                                  ? 'Combined ZIP'
                                  : 'Downloading...'
                              }
                              status={
                                !isDownloadingCombinedZIP
                                  ? 'finished'
                                  : 'active'
                              }
                              aria-live={'assertive'}
                            />
                          )}
                        </Button>
                        {filteredEnquiryDetails.permalink && (
                          <Button
                            className="permalink-btn"
                            kind="tertiary"
                            renderIcon={Export16}
                            tooltipAlignment="end"
                            iconDescription="Share link"
                            hasIconOnly
                            onClick={() => handleShareLinkButtonClick()}
                          />
                        )}
                      </section>
                    </>
                  )}
                  {filteredEnquiryDetails.is_ignore && (
                    <section>
                      <UpgradePlanSection
                        viewConfirmationPDF={
                          confirmationPdfId
                            ? () => viewAttachment(confirmationPdfId)
                            : undefined
                        }
                      />
                    </section>
                  )}
                </div>
              </div>
            )}
          {!isLoadingEnquiryDetails && filteredEnquiryDetails.permalink && (
            <PermalinkSection
              url={filteredEnquiryDetails.permalink}
              onClose={() => setShowPermalink(false)}
            />
          )}
          {(enquiryIdDetails === 0 || isLoadingEnquiryDetails) && (
            <div className="loading-details">
              <Loading withOverlay={false} />
            </div>
          )}
          {showMoreActions && (
            <MoreActionsPanel
              onClose={() => setShowMoreActions(false)}
              showAutoRenewItem={showAutoRenewItemMenu}
              showEnableAutoRenew={showEnableAutoRenewMenu}
              showDuplicateItem={filteredEnquiryDetails.internal_create}
              confirmationPdfId={confirmationPdfId}
              viewAttachment={viewAttachment}
              uploadAttachment={uploadAttachment}
              archiveEnquiry={archiveEnquiry}
              onAutoRenewingClick={handleAutoRenewingClick}
              onEnquiryEditClick={() => setShowEditForm(true)}
              enquiryId={filteredEnquiryDetails.id}
              isArchive={!filteredEnquiryDetails.archived_at}
              moreActionPos={moreActionPos}
              onMoveEnquiryToTeamClick={handleMoveEnquiryToTeam}
              userRole={user.role_type_actual}
              isAutoRenew={showAutoRenewItemMenu}
              enquiryAutoRenewSeries={enquiryAutoRenewSeries}
              archiveSeriesHandler={handleArchiveSeriesClick}
              userId={user.id}
              enquiryUserId={filteredEnquiryDetails.user_id}
            />
          )}
        </section>
        {excludeFilesModalData?.files.length > 0 && (
          <ExcludeFilesModal
            isModalOpen={excludeFilesModalOpen}
            setIsModalOpen={setExcludeFilesModalOpen}
            excludeFilesModalData={excludeFilesModalData}
            onExcludeButtonClick={handleExcludeButtonClick}
            isUpdatingAttachments={isUpdatingAttachments}
            updateAttachments={updateAttachments}
          />
        )}
      </div>
    </>
  );
};

EnquiryDetails.displayName = 'EnquiryDetails';

type Props = {
  handleCloseIconClick?: any;
  enquiryIdDetails?: number;
  setEnquiryIdDetails?: Function;
  isListView: boolean;
  isAnonymousView?: boolean;
  anonymousEnquiryDetailsData?: any;
  vhToken?: string;
  plToken?: string;
  setSearchText?: Function;
  setSearchInputValue?: Function;
  setIsListView?: Function;
  refreshMap?: Function;
};

export default React.memo(EnquiryDetails);
